/**
 * Supabase istemcisi — **lazy** dynamic import ile yüklenir.
 *
 * Strateji:
 *  - `@supabase/supabase-js` modülü (~50 KB gzip) eager bundle'da yer almaz.
 *  - `getSupabase()` ilk çağrıda dynamic import eder, sonra singleton döner.
 *  - Anonim ziyaretçiler (landing, /kvkk vs.) hiç indirmez.
 *  - Auth bootstrap, kullanıcı localStorage'da Supabase token'ı varsa
 *    veya kullanıcı auth aksiyonu (login/signup/...) yaptığında yükler.
 *
 * Eski `import { supabase }` API'sı kaldırıldı — tüm çağrı yerleri
 * `const s = await getSupabase(); s.auth.X(...)` formuna geçti.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  // eslint-disable-next-line no-console
  console.error(
    'Supabase ortam değişkenleri eksik. .env.local dosyasında VITE_SUPABASE_URL ve VITE_SUPABASE_ANON_KEY tanımlı olmalı.',
  );
}

let instance: SupabaseClient | null = null;
let loading: Promise<SupabaseClient> | null = null;

/**
 * Supabase istemcisini lazy yükle ve döndür. Idempotent — ilk çağrıdan
 * sonra cache'lenmiş singleton döner.
 */
export async function getSupabase(): Promise<SupabaseClient> {
  if (instance) return instance;
  if (loading) return loading;

  loading = (async () => {
    const { createClient } = await import('@supabase/supabase-js');
    instance = createClient(
      supabaseUrl ?? 'http://localhost:0',
      supabaseAnonKey ?? '',
      {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
          flowType: 'pkce',
        },
      },
    );
    return instance;
  })();

  return loading;
}

/**
 * Kullanıcı tarayıcısında daha önce login olmuş mu? (anonim heuristik)
 * Supabase v2 localStorage anahtarı `sb-<project-ref>-auth-token` formatında.
 * Bu kontrol sayesinde anonim ziyaretçide Supabase'i hiç yüklemiyoruz.
 */
export function hasPersistedSupabaseSession(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        return true;
      }
    }
  } catch {
    // localStorage erişimi engellenmiş olabilir (private mode vs.) — defansif
  }
  return false;
}
